import { useCallback, useContext, useEffect, useState } from 'react'
import { fetchData } from '../../../global/utils/fetch'
import { getPrompts, updatePromptApi } from '../../api'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import styles from './styles.module.css'

interface PromptItem {
  objectId: string
  promptKey: string
  promptText: string
  updatedBy: string | null
  updated: string
}

const PROMPT_LABELS: Record<string, string> = {
  classification: 'Classification (Input Gate)',
  generation: 'Answer Generation',
}

function PromptEditor() {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [prompts, setPrompts] = useState<PromptItem[]>([])
  const [selectedKey, setSelectedKey] = useState<string>('')
  const [editText, setEditText] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)

  const loadPrompts = useCallback(async () => {
    try {
      const data = await fetchData<PromptItem[]>(getPrompts())
      setPrompts(data)
      if (data.length > 0 && !selectedKey) {
        setSelectedKey(data[0].promptKey)
        setEditText(data[0].promptText)
      }
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to load prompts.',
        severity: 'error',
      })
    }
  }, [])

  useEffect(() => {
    loadPrompts()
  }, [loadPrompts])

  useEffect(() => {
    const prompt = prompts.find((p) => p.promptKey === selectedKey)
    if (prompt) {
      setEditText(prompt.promptText)
    }
  }, [selectedKey, prompts])

  const selectedPrompt = prompts.find((p) => p.promptKey === selectedKey)

  async function handleSave() {
    if (!selectedKey) return
    setIsSaving(true)
    try {
      await fetchData(
        updatePromptApi(selectedKey, { promptText: editText }),
      )
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Prompt saved.',
        severity: 'success',
      })
      loadPrompts()
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to save prompt.',
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={styles.container}>
      <select
        className={styles.select}
        value={selectedKey}
        onChange={(e) => setSelectedKey(e.target.value)}
      >
        {prompts.map((p) => (
          <option key={p.promptKey} value={p.promptKey}>
            {PROMPT_LABELS[p.promptKey] || p.promptKey}
          </option>
        ))}
      </select>

      <textarea
        className={styles.textarea}
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        rows={16}
      />

      {selectedPrompt && (
        <span className={styles.meta}>
          Last updated{selectedPrompt.updatedBy ? ` by: ${selectedPrompt.updatedBy}` : ''} |{' '}
          {new Date(selectedPrompt.updated).toLocaleDateString()}
        </span>
      )}

      <div className={styles.footer}>
        <PrimaryButton
          buttonName={isSaving ? 'Saving...' : 'Save'}
          onClick={handleSave}
          disabled={isSaving}
        />
      </div>
    </div>
  )
}

export default PromptEditor
